import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { api, apiError, mediaUrl } from "@/lib/api";
import { useI18n, DOWNLOAD_MODES } from "@/lib/i18n";
import { GoldButton } from "@/components/Brand";
import { X, Image as ImageIcon, Mail, KeyRound, MessageCircle, Copy, Facebook, Trash2, Upload } from "lucide-react";
import { FocusButton } from "@/components/admin/FocusDialog";

const field = "min-h-[42px] w-full border border-[#C69A3B]/60 bg-white px-2 text-sm";

export default function AdminEvents({ refreshToken, onChange }) {
  const { t, lang, localized } = useI18n();
  const coverRefs = useRef({});
  const supportRefs = useRef({});
  const [events, setEvents] = useState([]);
  const [expanded, setExpanded] = useState(null);
  const [albums, setAlbums] = useState([]);
  const [newAlbum, setNewAlbum] = useState({ name_es: "", name_en: "" });
  const [coverPicker, setCoverPicker] = useState(null);
  const [coverMedia, setCoverMedia] = useState([]);
  const [passwords, setPasswords] = useState({});
  const [invitePanel, setInvitePanel] = useState(null);
  const [inviteEmails, setInviteEmails] = useState("");
  const [inviteContacts, setInviteContacts] = useState("");
  const [inviteChannel, setInviteChannel] = useState("share");
  const [inviteLang, setInviteLang] = useState("both");
  const [invites, setInvites] = useState([]);
  const [shareLinks, setShareLinks] = useState([]);
  const [shareKit, setShareKit] = useState(null);
  const [sending, setSending] = useState(false);

  const load = useCallback(() => {
    api.get("/admin/events").then((r) => setEvents(r.data)).catch(() => {});
  }, []);

  useEffect(load, [load, refreshToken]);

  const loadAlbums = async (eventId) => {
    const { data } = await api.get(`/admin/events/${eventId}/albums`);
    setAlbums(data);
  };

  const patch = async (id, updates, message) => {
    try {
      await api.patch(`/admin/events/${id}`, updates);
      load();
      onChange?.();
      toast.success(message || t("btn.save"));
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    }
  };

  const renameAlbum = async (album, updates, previous) => {
    const value = Object.values(updates)[0].trim();
    if (value === (previous || "")) return;
    if (!value && updates.name_es !== undefined) return;
    try {
      await api.patch(`/admin/albums/${album.id}`, updates);
      toast.success(t("admin.albumRenamed"));
      loadAlbums(expanded);
      onChange?.();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    }
  };

  const deleteAlbum = async (album) => {
    const total = (album.photo_count || 0) + (album.video_count || 0);
    const question = total
      ? t("admin.albumDeleteWithMedia").replace("{n}", total)
      : t("admin.albumDeleteConfirm");
    if (!window.confirm(`${album.name_es}\n\n${question}`)) return;
    try {
      await api.delete(`/admin/albums/${album.id}`);
      toast.success(t("admin.albumDeleted"));
      loadAlbums(expanded);
      onChange?.();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    }
  };

  const openCoverPicker = async (album) => {
    setCoverPicker(album);
    setCoverMedia([]);
    const { data } = await api.get("/admin/media", {
      params: { album_id: album.id, kind: "photo", page_size: 400 },
    });
    setCoverMedia(data.media);
  };

  const setCover = async (media) => {
    await api.patch(`/admin/albums/${coverPicker.id}`, {
      cover_media_id: media.id,
      cover_image: media.external_url || null,
    });
    toast.success(t("admin.coverSet"));
    setCoverPicker(null);
    loadAlbums(expanded);
    onChange?.();
  };

  const clearCover = async () => {
    await api.patch(`/admin/albums/${coverPicker.id}`, { cover_media_id: null, cover_image: null });
    toast.success(t("admin.coverCleared"));
    setCoverPicker(null);
    loadAlbums(expanded);
    onChange?.();
  };

  const loadShareKit = async (event, language) => {
    try {
      const { data } = await api.get(`/admin/events/${event.id}/share`, { params: { language } });
      setShareKit(data);
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    }
  };

  const openInvites = async (event) => {
    setInvitePanel(event);
    setShareKit(null);
    const { data } = await api.get(`/admin/events/${event.id}/invites`);
    setInvites(data);
  };

  const sendInvites = async () => {
    setSending(true);
    try {
      const body = { language: inviteLang, channel: inviteChannel };
      if (inviteChannel === "email") {
        body.emails = inviteEmails
          .split(/[\s,;]+/)
          .map((e) => e.trim())
          .filter(Boolean);
        if (!body.emails.length) return;
      } else {
        body.contacts = inviteContacts
          .split(/\r?\n/)
          .map((row) => row.trim())
          .filter(Boolean)
          .map((row) => {
            const [name, phone] = row.split(/[,;]/).map((p) => (p || "").trim());
            return { name: name || phone || "Invitado", phone: phone || null };
          });
        if (!body.contacts.length) return;
      }
      const { data } = await api.post(`/admin/events/${invitePanel.id}/invites`, body);
      if (inviteChannel === "email" && data.sent.length)
        toast.success(`${data.sent.length} ${t("admin.invitesSent")}`);
      if (inviteChannel === "whatsapp" && data.links.length)
        toast.success(`${data.links.length} ${t("admin.linksReady")}`);
      if (data.failed.length) toast.error(data.failed.map((f) => `${f.email}: ${f.error}`).join(" · "));
      setShareLinks(data.links || []);
      setInviteEmails("");
      setInviteContacts("");
      openInvites(invitePanel);
      load();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  const copyText = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(t("gallery.linkCopied"));
      return;
    } catch {
      /* falls through to the manual copy below */
    }
    const area = document.createElement("textarea");
    area.value = text;
    area.style.position = "fixed";
    area.style.opacity = "0";
    document.body.appendChild(area);
    area.select();
    const ok = document.execCommand("copy");
    document.body.removeChild(area);
    if (ok) toast.success(t("gallery.linkCopied"));
    else toast.error(t("admin.copyFailed"));
  };

  const uploadSupportQr = async (event, file) => {
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post("/admin/site-images", form);
      await patch(event.id, { support_qr: data.url, support_enabled: true }, t("admin.supportSaved"));
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    }
  };

  const uploadEventCover = async (event, file) => {
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post("/admin/site-images", form);
      await patch(event.id, { cover_image: data.url }, t("admin.coverSet"));
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    }
  };

  const reshare = async (inviteId) => {    try {
      const { data } = await api.get(`/admin/invites/${inviteId}/share`);
      setShareLinks([data]);
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    }
  };

  return (
    <div data-testid="admin-events-panel">
      <div className="overflow-x-auto border border-[#C69A3B]/30 bg-white">
        <table className="w-full min-w-[1000px] text-sm">
          <thead className="bg-[#F6F0E4] text-left">
            <tr className="label-mono">
              <th className="p-3">Evento</th>
              <th className="p-3">{t("common.date")}</th>
              <th className="p-3">{t("common.privacy")}</th>
              <th className="p-3">{t("admin.password")}</th>
              <th className="p-3">{t("common.downloads")}</th>
              <th className="p-3">{t("common.watermark")}</th>
              <th className="p-3">Estado</th>
              <th className="p-3" />
            </tr>
          </thead>
          <tbody>
            {events.map((ev) => (
              <tr key={ev.id} data-testid={`admin-event-row-${ev.slug}`} className="border-t border-[#D8D3CB]">
                <td className="p-3">
                  <div className="flex items-start gap-3">
                    <button
                      type="button"
                      data-testid={`admin-event-cover-${ev.slug}`}
                      onClick={() => coverRefs.current[ev.id]?.click()}
                      title={t("admin.eventCover")}
                      className="h-14 w-20 shrink-0 overflow-hidden border border-[#C69A3B]/50 bg-[#EFEAE0]"
                    >
                      {ev.cover_image ? (
                        <img src={ev.cover_image} alt="" className="h-full w-full object-cover" />
                      ) : (
                        <Upload size={14} className="mx-auto text-[#C69A3B]" />
                      )}
                    </button>
                    <input
                      ref={(el) => (coverRefs.current[ev.id] = el)}
                      type="file"
                      accept="image/*"
                      className="hidden"
                      data-testid={`admin-event-cover-input-${ev.slug}`}
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) uploadEventCover(ev, file);
                        e.target.value = "";
                      }}
                    />
                    <div>
                      <p className="font-medium">{localized(ev, "name")}</p>
                      <p className="text-xs text-[#5A5752]">/{ev.slug} · {ev.gallery_code} · {ev.media_count} / {ev.album_count}</p>
                      <button
                        type="button"
                        data-testid={`admin-event-cover-set-${ev.slug}`}
                        onClick={() => coverRefs.current[ev.id]?.click()}
                        className="mt-1 text-xs text-[#275E64] underline"
                      >
                        {ev.cover_image ? t("admin.coverChange") : t("admin.coverUpload")}
                      </button>
                      {ev.cover_image && (
                        <button
                          type="button"
                          data-testid={`admin-event-cover-clear-${ev.slug}`}
                          onClick={() => patch(ev.id, { cover_image: null }, t("admin.coverCleared"))}
                          className="ml-3 mt-1 text-xs text-[#8F3329] underline"
                        >
                          {t("admin.coverClear")}
                        </button>
                      )}
                      {ev.cover_image && (
                        <span className="ml-3 inline-block">
                          <FocusButton
                            url={ev.cover_image}
                            aspect="aspect-[4/3]"
                            testId={`admin-event-focus-${ev.slug}`}
                          />
                        </span>
                      )}
                      <button
                        type="button"
                        data-testid={`admin-event-support-${ev.slug}`}
                        onClick={() => supportRefs.current[ev.id]?.click()}
                        className="ml-3 mt-1 text-xs text-[#275E64] underline"
                      >
                        {t("admin.support")}
                      </button>
                      <input
                        ref={(el) => (supportRefs.current[ev.id] = el)}
                        type="file"
                        accept="image/*"
                        className="hidden"
                        data-testid={`admin-event-support-input-${ev.slug}`}
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file) uploadSupportQr(ev, file);
                          e.target.value = "";
                        }}
                      />
                      {ev.support_qr && (
                        <label className="mt-2 flex flex-wrap items-center gap-2 text-xs text-[#5A5752]">
                          <input
                            type="checkbox"
                            data-testid={`admin-event-support-toggle-${ev.slug}`}
                            checked={Boolean(ev.support_enabled)}
                            onChange={(e) =>
                              patch(ev.id, { support_enabled: e.target.checked }, t("admin.supportSaved"))
                            }
                          />
                          {t("admin.supportEnabled")}
                          <button
                            type="button"
                            data-testid={`admin-event-support-clear-${ev.slug}`}
                            onClick={() =>
                              patch(ev.id, { support_qr: null, support_enabled: false }, t("admin.supportSaved"))
                            }
                            className="text-[#8F3329] underline"
                          >
                            {t("admin.supportRemove")}
                          </button>
                        </label>
                      )}
                    </div>
                  </div>
                </td>
                <td className="p-3 text-xs">{ev.event_date}</td>
                <td className="p-3">
                  <select
                    className={field}
                    data-testid={`admin-event-privacy-${ev.slug}`}
                    value={ev.privacy}
                    onChange={(e) => patch(ev.id, { privacy: e.target.value })}
                  >
                    <option value="public">{t("privacy.public")}</option>
                    <option value="password">{t("privacy.password")}</option>
                    <option value="private">{t("privacy.private")}</option>
                  </select>
                </td>
                <td className="p-3">
                  {ev.privacy === "password" ? (
                    <div className="flex items-center gap-2">
                      <input
                        type="text"
                        placeholder={t("admin.newPassword")}
                        data-testid={`admin-event-password-input-${ev.slug}`}
                        value={passwords[ev.id] || ""}
                        onChange={(e) => setPasswords((p) => ({ ...p, [ev.id]: e.target.value }))}
                        className="min-h-[42px] w-36 border border-[#C69A3B]/60 px-2 text-sm"
                      />
                      <button
                        type="button"
                        data-testid={`admin-event-password-save-${ev.slug}`}
                        disabled={!(passwords[ev.id] || "").trim()}
                        onClick={async () => {
                          await patch(ev.id, { password: passwords[ev.id].trim() }, t("admin.passwordUpdated"));
                          setPasswords((p) => ({ ...p, [ev.id]: "" }));
                        }}
                        className="grid h-[42px] w-[42px] place-items-center border border-[#C69A3B] text-[#8F3329] disabled:opacity-40"
                        aria-label={t("admin.newPassword")}
                      >
                        <KeyRound size={15} />
                      </button>
                    </div>
                  ) : (
                    <span className="text-xs text-[#5A5752]">—</span>
                  )}
                </td>
                <td className="p-3">
                  <select
                    className={field}
                    data-testid={`admin-event-download-${ev.slug}`}
                    value={ev.download_mode}
                    onChange={(e) => patch(ev.id, { download_mode: e.target.value })}
                  >
                    {Object.keys(DOWNLOAD_MODES).map((m) => (
                      <option key={m} value={m}>{DOWNLOAD_MODES[m][lang]}</option>
                    ))}
                  </select>
                </td>
                <td className="p-3">
                  <input
                    type="checkbox"
                    data-testid={`admin-event-watermark-${ev.slug}`}
                    checked={Boolean(ev.watermark)}
                    onChange={(e) => patch(ev.id, { watermark: e.target.checked })}
                  />
                </td>
                <td className="p-3">
                  <button
                    type="button"
                    data-testid={`admin-event-publish-${ev.slug}`}
                    onClick={() => patch(ev.id, { published: !ev.published })}
                    className={`border px-3 py-1 text-xs ${
                      ev.published ? "border-[#275E64] text-[#275E64]" : "border-[#C69A3B] text-[#8F3329]"
                    }`}
                  >
                    {ev.published ? t("common.published") : t("common.draft")}
                  </button>
                </td>
                <td className="p-3">
                  <div className="flex flex-col gap-1">
                    <button
                      type="button"
                      data-testid={`admin-event-albums-${ev.slug}`}
                      onClick={async () => {
                        const next = expanded === ev.id ? null : ev.id;
                        setExpanded(next);
                        if (next) await loadAlbums(ev.id);
                      }}
                      className="text-left text-xs text-[#8F3329] underline"
                    >
                      {t("events.albums")}
                    </button>
                    <button
                      type="button"
                      data-testid={`admin-event-invites-${ev.slug}`}
                      onClick={() => openInvites(ev)}
                      className="inline-flex items-center gap-1 text-left text-xs text-[#275E64] underline"
                    >
                      <Mail size={12} /> {t("admin.invites")}
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {expanded && (
        <div className="mt-6 border border-[#C69A3B]/40 bg-white p-6" data-testid="admin-albums-panel">
          <p className="label-mono">{t("events.albums")}</p>
          <p className="mt-2 text-xs text-[#5A5752]">{t("admin.albumsHelp")}</p>
          <ul className="mt-4 space-y-2 text-sm">
            {albums.map((al) => (
              <li key={al.id} className="flex flex-wrap items-center gap-3 border-b border-[#D8D3CB] py-2">
                <button
                  type="button"
                  data-testid={`admin-album-cover-thumb-${al.slug}`}
                  onClick={() => openCoverPicker(al)}
                  title={t("admin.cover")}
                  className="h-14 w-14 shrink-0 overflow-hidden border border-[#C69A3B]/50 bg-[#EFEAE0]"
                >
                  {al.cover_media_id || al.cover_image ? (
                    <img
                      src={al.cover_media_id ? mediaUrl({ id: al.cover_media_id }, "thumb") : al.cover_image}
                      alt={al.name_es}
                      className="h-full w-full object-cover"
                    />
                  ) : (
                    <ImageIcon size={16} className="mx-auto text-[#C69A3B]" />
                  )}
                </button>
                <input
                  className={`${field} flex-1 min-w-[150px]`}
                  data-testid={`admin-album-name-es-${al.slug}`}
                  defaultValue={al.name_es || ""}
                  placeholder="Nombre — Español"
                  onBlur={(e) => renameAlbum(al, { name_es: e.target.value }, al.name_es)}
                />
                <input
                  className={`${field} flex-1 min-w-[150px]`}
                  data-testid={`admin-album-name-en-${al.slug}`}
                  defaultValue={al.name_en || ""}
                  placeholder="Name — English"
                  onBlur={(e) => renameAlbum(al, { name_en: e.target.value }, al.name_en)}
                />
                <span className="text-xs text-[#5A5752]">
                  {al.photo_count} {t("events.photos")} · {al.video_count} {t("events.videos")}
                </span>
                <button
                  type="button"
                  data-testid={`admin-album-cover-${al.slug}`}
                  onClick={() => openCoverPicker(al)}
                  className="inline-flex items-center gap-1 border border-[#C69A3B] px-3 py-1.5 text-xs text-[#8F3329] transition-colors duration-300 hover:bg-[#8F3329] hover:text-[#FAFAF7]"
                >
                  <ImageIcon size={12} /> {t("admin.cover")}
                </button>
                {(al.cover_media_id || al.cover_image) && (
                  <FocusButton
                    url={al.cover_media_id ? mediaUrl({ id: al.cover_media_id }, "thumb") : al.cover_image}
                    focusKey={`album-${al.id}`}
                    aspect="aspect-[4/3]"
                    testId={`admin-album-focus-${al.slug}`}
                  />
                )}
                <label className="flex items-center gap-1 text-xs">
                  <input
                    type="checkbox"
                    data-testid={`admin-album-download-${al.slug}`}
                    checked={Boolean(al.download_enabled)}
                    onChange={async (e) => {
                      await api.patch(`/admin/albums/${al.id}`, { download_enabled: e.target.checked });
                      loadAlbums(expanded);
                    }}
                  />
                  {t("common.downloads")}
                </label>
                <label className="flex items-center gap-1 text-xs">
                  <input
                    type="checkbox"
                    data-testid={`admin-album-watermark-${al.slug}`}
                    checked={Boolean(al.watermark)}
                    onChange={async (e) => {
                      await api.patch(`/admin/albums/${al.id}`, { watermark: e.target.checked });
                      loadAlbums(expanded);
                    }}
                  />
                  {t("common.watermark")}
                </label>
                <button
                  type="button"
                  data-testid={`admin-album-delete-${al.slug}`}
                  onClick={() => deleteAlbum(al)}
                  className="inline-flex items-center gap-1 border border-[#8F3329] px-3 py-1.5 text-xs text-[#8F3329] transition-colors duration-300 hover:bg-[#8F3329] hover:text-[#FAFAF7]"
                >
                  <Trash2 size={12} /> {t("btn.delete")}
                </button>
              </li>
            ))}
          </ul>
          <div className="mt-5 grid gap-2 sm:grid-cols-[1fr_1fr_auto]">
            <input
              className={field}
              placeholder="Álbum — Español"
              data-testid="admin-new-album-es"
              value={newAlbum.name_es}
              onChange={(e) => setNewAlbum((a) => ({ ...a, name_es: e.target.value }))}
            />
            <input
              className={field}
              placeholder="Album — English"
              data-testid="admin-new-album-en"
              value={newAlbum.name_en}
              onChange={(e) => setNewAlbum((a) => ({ ...a, name_en: e.target.value }))}
            />
            <GoldButton
              data-testid="admin-create-album-btn"
              disabled={!newAlbum.name_es}
              onClick={async () => {
                await api.post(`/admin/events/${expanded}/albums`, newAlbum);
                setNewAlbum({ name_es: "", name_en: "" });
                loadAlbums(expanded);
                onChange?.();
              }}
            >
              +
            </GoldButton>
          </div>
        </div>
      )}

      {coverPicker && (
        <div
          role="dialog"
          aria-modal="true"
          data-testid="cover-picker-modal"
          className="fixed inset-0 z-[80] flex flex-col bg-[#161616]/95 p-4 sm:p-10"
        >
          <div className="flex items-center justify-between">
            <div>
              <p className="label-mono">
                {t("admin.cover")} — {localized(coverPicker, "name")}
              </p>
              <p className="mt-1 text-xs text-[#E8D8B6]">{t("admin.coverHelp")}</p>
            </div>
            <button
              type="button"
              data-testid="cover-picker-clear"
              onClick={clearCover}
              className="mr-3 min-h-[44px] border border-[#C69A3B]/60 px-4 text-xs text-[#F6F0E4] transition-colors duration-300 hover:bg-[#8F3329]"
            >
              {t("admin.coverClear")}
            </button>
            <button
              type="button"
              data-testid="cover-picker-close"
              aria-label={t("nav.close")}
              onClick={() => setCoverPicker(null)}
              className="grid h-11 w-11 place-items-center border border-[#C69A3B]/60 text-[#F6F0E4]"
            >
              <X size={17} />
            </button>
          </div>
          <div className="mt-6 grid flex-1 auto-rows-min content-start grid-cols-2 gap-3 overflow-y-auto sm:grid-cols-4 lg:grid-cols-6">
            {coverMedia.length === 0 && (
              <p className="col-span-full text-sm text-[#E8D8B6]">{t("gallery.empty")}</p>
            )}
            {coverMedia.map((m) => (
              <button
                key={m.id}
                type="button"
                data-testid={`cover-option-${m.id}`}
                onClick={() => setCover(m)}
                className="h-fit overflow-hidden border border-[#C69A3B]/40 transition-colors duration-300 hover:border-[#C69A3B]"
              >
                <img
                  src={m.external_url || mediaUrl(m, "thumb")}
                  alt={m.filename}
                  loading="lazy"
                  className="aspect-square w-full object-cover"
                />
              </button>
            ))}
          </div>
        </div>
      )}

      {invitePanel && (
        <div className="mt-6 border border-[#C69A3B]/40 bg-white p-6" data-testid="invites-panel">
          <div className="flex items-center justify-between">
            <p className="label-mono">
              {t("admin.invites")} — {localized(invitePanel, "name")}
            </p>
            <button
              type="button"
              data-testid="invites-close"
              onClick={() => setInvitePanel(null)}
              className="text-xs underline"
            >
              {t("nav.close")}
            </button>
          </div>
          <p className="mt-3 text-xs text-[#5A5752]">
            {inviteChannel === "share" ? t("admin.shareKitHelp") : t("admin.invitesHelp")}
          </p>

          <div className="mt-4 flex flex-wrap gap-2">
            {[["share", t("admin.channelShare")], ["email", t("admin.emailSecondary")]].map(([key, label]) => (
              <button
                key={key}
                type="button"
                data-testid={`invite-channel-${key}`}
                onClick={() => {
                  setInviteChannel(key);
                  setShareLinks([]);
                  setShareKit(null);
                }}
                className={`min-h-[44px] border px-4 text-sm transition-colors duration-300 ${
                  inviteChannel === key ? "border-[#8F3329] bg-[#8F3329] text-[#FAFAF7]" : "border-[#C69A3B]/50"
                }`}
              >
                {label}
              </button>
            ))}
          </div>

          {inviteChannel === "email" && (
            <textarea
              rows={3}
              data-testid="invite-emails-input"
              value={inviteEmails}
              onChange={(e) => setInviteEmails(e.target.value)}
              placeholder="maria@correo.com, jose@correo.com"
              className="mt-4 w-full border border-[#C69A3B]/60 p-3 text-sm"
            />
          )}

          <div className="mt-4 flex flex-wrap items-center gap-3">
            <select
              className="min-h-[44px] border border-[#C69A3B]/60 px-3 text-sm"
              data-testid="invite-language-select"
              value={inviteLang}
              onChange={(e) => {
                setInviteLang(e.target.value);
                if (inviteChannel === "share") loadShareKit(invitePanel, e.target.value);
              }}
            >
              <option value="both">Español + English</option>
              <option value="es">Español</option>
              <option value="en">English</option>
            </select>
            {inviteChannel === "share" ? (
              <GoldButton
                onClick={() => loadShareKit(invitePanel, inviteLang)}
                data-testid="share-generate-btn"
              >
                {t("admin.shareGenerate")}
              </GoldButton>
            ) : (
              <GoldButton
                onClick={sendInvites}
                disabled={sending || !inviteEmails.trim()}
                data-testid="invite-send-btn"
              >
                {sending ? t("common.loading") : t("admin.sendInvites")}
              </GoldButton>
            )}
          </div>

          {inviteChannel === "share" && shareKit && (
            <div className="mt-6 border border-[#C69A3B]/40 bg-[#F6F0E4] p-4" data-testid="share-kit">
              <p className="label-mono">{t("admin.shareKit")}</p>
              <p className="mt-2 break-all text-xs text-[#5A5752]" data-testid="share-kit-link">
                {shareKit.link}
              </p>

              <p className="label-mono mt-5">{t("admin.shareWhatsappMsg")}</p>
              <pre className="mt-2 whitespace-pre-wrap border border-[#C69A3B]/30 bg-white p-3 text-xs" data-testid="share-kit-message">
                {shareKit.message}
              </pre>
              <p className="label-mono mt-5">{t("admin.shareFacebookMsg")}</p>
              <pre className="mt-2 whitespace-pre-wrap border border-[#C69A3B]/30 bg-white p-3 text-xs" data-testid="share-kit-post">
                {shareKit.facebook_post}
              </pre>

              <div className="mt-4 flex flex-wrap gap-2">
                <a
                  href={shareKit.wa_url}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="share-kit-whatsapp"
                  className="inline-flex min-h-[40px] items-center gap-2 border border-[#275E64] px-4 text-xs text-[#275E64] transition-colors duration-300 hover:bg-[#275E64] hover:text-[#FAFAF7]"
                >
                  <MessageCircle size={13} /> WhatsApp
                </a>
                <a
                  href={shareKit.facebook_url}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="share-kit-facebook"
                  className="inline-flex min-h-[40px] items-center gap-2 border border-[#275E64] px-4 text-xs text-[#275E64] transition-colors duration-300 hover:bg-[#275E64] hover:text-[#FAFAF7]"
                >
                  <Facebook size={13} /> {t("admin.shareFacebook")}
                </a>
                <button
                  type="button"
                  data-testid="share-kit-copy-message"
                  onClick={() => copyText(shareKit.message)}
                  className="inline-flex min-h-[40px] items-center gap-2 border border-[#C69A3B] px-4 text-xs text-[#8F3329] transition-colors duration-300 hover:bg-[#8F3329] hover:text-[#FAFAF7]"
                >
                  <Copy size={13} /> {t("admin.copyMessage")}
                </button>
                <button
                  type="button"
                  data-testid="share-kit-copy-post"
                  onClick={() => copyText(shareKit.facebook_post)}
                  className="inline-flex min-h-[40px] items-center gap-2 border border-[#C69A3B] px-4 text-xs text-[#8F3329] transition-colors duration-300 hover:bg-[#8F3329] hover:text-[#FAFAF7]"
                >
                  <Copy size={13} /> {t("admin.copyPost")}
                </button>
                <button
                  type="button"
                  data-testid="share-kit-copy-link"
                  onClick={() => copyText(shareKit.link)}
                  className="inline-flex min-h-[40px] items-center gap-2 border border-[#C69A3B] px-4 text-xs text-[#8F3329] transition-colors duration-300 hover:bg-[#8F3329] hover:text-[#FAFAF7]"
                >
                  <Copy size={13} /> {t("admin.copyLink")}
                </button>
              </div>
            </div>
          )}

          {shareLinks.length > 0 && (
            <ul className="mt-6 space-y-3" data-testid="invite-share-list">
              {shareLinks.map((s) => (
                <li key={s.id} className="border border-[#C69A3B]/40 bg-[#F6F0E4] p-4 text-sm">
                  <p className="font-medium">{s.label}</p>
                  <p className="mt-1 break-all text-xs text-[#5A5752]">{s.link}</p>
                  <div className="mt-3 flex flex-wrap gap-2">
                    <a
                      href={s.wa_url}
                      target="_blank"
                      rel="noreferrer"
                      data-testid={`invite-whatsapp-${s.id}`}
                      className="inline-flex min-h-[40px] items-center gap-2 border border-[#275E64] px-4 text-xs text-[#275E64] transition-colors duration-300 hover:bg-[#275E64] hover:text-[#FAFAF7]"
                    >
                      <MessageCircle size={13} /> WhatsApp
                    </a>
                    <button
                      type="button"
                      data-testid={`invite-copy-message-${s.id}`}
                      onClick={() => copyText(s.message)}
                      className="inline-flex min-h-[40px] items-center gap-2 border border-[#C69A3B] px-4 text-xs text-[#8F3329] transition-colors duration-300 hover:bg-[#8F3329] hover:text-[#FAFAF7]"
                    >
                      <Copy size={13} /> {t("admin.copyMessage")}
                    </button>
                    <button
                      type="button"
                      data-testid={`invite-copy-link-${s.id}`}
                      onClick={() => copyText(s.link)}
                      className="inline-flex min-h-[40px] items-center gap-2 border border-[#C69A3B] px-4 text-xs text-[#8F3329] transition-colors duration-300 hover:bg-[#8F3329] hover:text-[#FAFAF7]"
                    >
                      <Copy size={13} /> {t("admin.copyLink")}
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
          <ul className="mt-6 space-y-2 text-sm" data-testid="invites-list">
            {invites.length === 0 && <li className="text-[#5A5752]">—</li>}
            {invites.map((inv) => (
              <li key={inv.id} className="flex flex-wrap items-center justify-between gap-2 border-b border-[#D8D3CB] py-2">
                <span>{inv.email || inv.name}</span>
                <span className="flex items-center gap-3">
                  <span className="label-mono">
                    {(inv.channel || "email") === "whatsapp" ? "WA" : "@"} · {inv.language.toUpperCase()} ·{" "}
                    {String(inv.created_at).slice(0, 10)} ·{" "}
                    {inv.redeemed_at ? t("admin.inviteUsed") : t("admin.inviteSent")}
                  </span>
                  <button
                    type="button"
                    data-testid={`invite-reshare-${inv.id}`}
                    onClick={() => reshare(inv.id)}
                    className="text-xs text-[#275E64] underline"
                  >
                    {t("admin.share")}
                  </button>
                  <button
                    type="button"
                    data-testid={`invite-revoke-${inv.id}`}
                    onClick={async () => {
                      await api.delete(`/admin/invites/${inv.id}`);
                      openInvites(invitePanel);
                      setShareLinks([]);
                      toast.success(t("admin.inviteRevoked"));
                    }}
                    className="text-xs text-[#8F3329] underline"
                  >
                    {t("admin.revoke")}
                  </button>
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
